import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  CircleCheck,
  Clock,
  Link2,
  Pencil,
  Plus,
  Search,
  Trash2,
    Users,
  X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import {
  JOB_CATEGORIES,
  ADMINISTRATION_TYPES,
  GALA_SPONSORS,
  SPECIAL_AWARD_CATEGORY,
  SPECIAL_AWARDS,
} from '../../constants/registration';
import './ManageGalaRegistrations.css';

type Tipo = 'finalista' | 'invitado_novagob' | 'invitado_madrid_talento' | 'invitado_patrocinador';
type Tab = 'listado' | 'acreditar';
type Estado = 'pendientes' | 'acreditados' | 'todos';

const TIPO_LABELS: Record<Tipo, string> = {
  finalista: 'Finalista',
  invitado_novagob: 'Invitado NovaGob',
  invitado_madrid_talento: 'Invitado Madrid Talento',
  invitado_patrocinador: 'Invitado patrocinador',
};

const TIPO_SHORT: Record<Tipo, string> = {
  finalista: 'Finalistas',
  invitado_novagob: 'NovaGob',
  invitado_madrid_talento: 'Madrid Talento',
  invitado_patrocinador: 'Patrocinadores',
};

interface Companion {
  id: string;
  acreditado: boolean;
  nombre: string;
  apellidos: string;
  email: string;
}

interface Registration {
  id: string;
  tipo: Tipo;
  nombre: string;
  apellidos: string;
  email: string;
  telefono: string | null;
  cargo: string | null;
  institucion: string | null;
  categoria_puesto: string | null;
  tipo_administracion: string | null;
  categoria_premio: string | null;
  premio_especial: string | null;
  patrocinador: string | null;
  acreditado: boolean;
  created_at: string;
  companions: Companion[];
}

const MAX_COMPANIONS = 2;

interface EditCompanion {
  key: string;
  id: string | null;
  nombre: string;
  apellidos: string;
  email: string;
}

interface EditForm {
  companions: EditCompanion[];
  tipo: Tipo;
  nombre: string;
  apellidos: string;
  email: string;
  telefono: string;
  cargo: string;
  institucion: string;
  categoriaPuesto: string;
  tipoAdministracion: string;
  categoriaPremio: string;
  premioEspecial: string;
  patrocinador: string;
}

const newId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const toEditForm = (r: Registration): EditForm => ({
  companions: r.companions.map((c) => ({
    key: c.id,
    id: c.id,
    nombre: c.nombre,
    apellidos: c.apellidos,
    email: c.email,
  })),
  tipo: r.tipo,
  nombre: r.nombre,
  apellidos: r.apellidos,
  email: r.email,
  telefono: r.telefono || '',
  cargo: r.cargo || '',
  institucion: r.institucion || '',
  categoriaPuesto: r.categoria_puesto || '',
  tipoAdministracion: r.tipo_administracion || '',
  categoriaPremio: r.categoria_premio || '',
  premioEspecial: r.premio_especial || '',
  patrocinador: r.patrocinador || '',
});

// Píldora de tipo: añade la categoría del premio (finalistas) o el patrocinador.
const tipoLabel = (r: Registration) => {
  const premio =
    r.categoria_premio === SPECIAL_AWARD_CATEGORY && r.premio_especial
      ? `${SPECIAL_AWARD_CATEGORY} ${r.premio_especial}`
      : r.categoria_premio;
  const extra = r.tipo === 'finalista' ? premio : r.tipo === 'invitado_patrocinador' ? r.patrocinador : null;
  return extra ? `${TIPO_LABELS[r.tipo]} · ${extra}` : TIPO_LABELS[r.tipo];
};

// Búsqueda sin tildes ni mayúsculas: en la puerta se teclea rápido y sin acentos.
const norm = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const matches = (r: Registration, term: string) => {
  const t = norm(term.trim());
  if (!t) return true;
  const haystack = norm(
    [
      r.nombre,
      r.apellidos,
      r.email,
      r.institucion || '',
      r.cargo || '',
      r.categoria_premio || '',
      r.premio_especial || '',
      r.patrocinador || '',
      ...r.companions.map((c) => `${c.nombre} ${c.apellidos}`),
    ].join(' ')
  );
  return t.split(/\s+/).every((word) => haystack.includes(word));
};

const initials = (r: Registration) =>
  `${r.nombre.charAt(0)}${r.apellidos.charAt(0)}`.toUpperCase() || '?';

const partySize = (r: Registration) => 1 + r.companions.length;

const partyDone = (r: Registration) =>
  (r.acreditado ? 1 : 0) + r.companions.filter((c) => c.acreditado).length;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const ManageGalaRegistrations: React.FC = () => {
  const { isNovagobTeam } = useAuth();

  const [tab, setTab] = useState<Tab>('listado');
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState<Tipo | 'todos'>('todos');

  const [acreditarSearch, setAcreditarSearch] = useState('');
  const [estado, setEstado] = useState<Estado>('pendientes');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const [editing, setEditing] = useState<Registration | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);

  const [premioCategories, setPremioCategories] = useState<string[]>([]);
  const [toast, setToast] = useState<{ msg: string; undo?: () => void } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const [deleting, setDeleting] = useState<Registration | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const dialogRef = useRef<HTMLDialogElement>(null);
  const deleteDialogRef = useRef<HTMLDialogElement>(null);
  const tabRefs = useRef<Record<Tab, HTMLButtonElement | null>>({ listado: null, acreditar: null });

  useEffect(() => {
    if (isNovagobTeam) loadData();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNovagobTeam]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (editing && !dialog.open) dialog.showModal();
    if (!editing && dialog.open) dialog.close();
  }, [editing]);

  useEffect(() => {
    const dialog = deleteDialogRef.current;
    if (!dialog) return;
    if (deleting && !dialog.open) dialog.showModal();
    if (!deleting && dialog.open) dialog.close();
  }, [deleting]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.undo ? 6000 : 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const notify = (msg: string, undo?: () => void) => setToast({ msg, undo });

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: regs, error: regsError } = await supabase
        .schema('premios')
        .from('gala_registrations')
        .select(
          'id, tipo, nombre, apellidos, email, telefono, cargo, institucion, categoria_puesto, tipo_administracion, categoria_premio, premio_especial, patrocinador, acreditado, created_at'
        )
        .order('created_at', { ascending: false });
      if (regsError) throw regsError;

      const { data: cats } = await supabase.schema('premios').from('categories').select('name').order('name');
      setPremioCategories((cats || []).map((c: { name: string }) => c.name));

      const { data: companions, error: companionsError } = await supabase
        .schema('premios')
        .from('gala_companions')
        .select('id, registration_id, nombre, apellidos, email, acreditado')
        .order('created_at', { ascending: true });
      if (companionsError) throw companionsError;

      const byReg = new Map<string, Companion[]>();
      (companions || []).forEach((c) => {
        const list = byReg.get(c.registration_id) || [];
        list.push({
          id: c.id,
          acreditado: !!c.acreditado,
          nombre: c.nombre,
          apellidos: c.apellidos,
          email: c.email,
        });
        byReg.set(c.registration_id, list);
      });

      setRegistrations((regs || []).map((r) => ({ ...r, companions: byReg.get(r.id) || [] })));
    } catch (err) {
      console.error('Error al cargar inscripciones a la gala:', err);
      setError('No se han podido cargar las inscripciones.');
    } finally {
      setLoading(false);
    }
  };

  // Cada persona (titular o acompañante) se acredita por separado: pueden llegar a horas distintas.
  const setAcreditado = async (reg: Registration, comp: Companion | null, next: boolean, isUndo = false) => {
    const rowId = comp ? comp.id : reg.id;
    setUpdatingId(rowId);
    try {
      const { error: updateError } = await supabase
        .schema('premios')
        .from(comp ? 'gala_companions' : 'gala_registrations')
        .update({ acreditado: next, acreditado_at: next ? new Date().toISOString() : null })
        .eq('id', rowId);
      if (updateError) throw updateError;

      setRegistrations((rs) =>
        rs.map((r) => {
          if (r.id !== reg.id) return r;
          return comp
            ? { ...r, companions: r.companions.map((c) => (c.id === comp.id ? { ...c, acreditado: next } : c)) }
            : { ...r, acreditado: next };
        })
      );
      const who = comp ? `${comp.nombre} ${comp.apellidos}` : `${reg.nombre} ${reg.apellidos}`;
      notify(
        next ? `${who} acreditado/a` : `Acreditación retirada a ${who}`,
        isUndo ? undefined : () => setAcreditado(reg, comp, !next, true)
      );
    } catch (err) {
      console.error('Error al actualizar acreditación:', err);
      setError('No se ha podido actualizar la acreditación. Inténtalo de nuevo.');
    } finally {
      setUpdatingId(null);
    }
  };

  const openEdit = (reg: Registration) => {
    setEditing(reg);
    setEditForm(toEditForm(reg));
  };

  const closeEdit = () => {
    setEditing(null);
    setEditForm(null);
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing || !editForm) return;
    const isFinalista = editForm.tipo === 'finalista';
    const patch = {
      tipo: editForm.tipo,
      nombre: editForm.nombre.trim(),
      apellidos: editForm.apellidos.trim(),
      email: editForm.email.trim(),
      telefono: isFinalista ? editForm.telefono.trim() || null : null,
      cargo: editForm.cargo.trim() || null,
      institucion: editForm.institucion.trim() || null,
      categoria_puesto: isFinalista ? null : editForm.categoriaPuesto || null,
      tipo_administracion: isFinalista ? null : editForm.tipoAdministracion || null,
      categoria_premio: isFinalista ? editForm.categoriaPremio || null : null,
      premio_especial:
        isFinalista && editForm.categoriaPremio === SPECIAL_AWARD_CATEGORY ? editForm.premioEspecial || null : null,
      patrocinador: editForm.tipo === 'invitado_patrocinador' ? editForm.patrocinador || null : null,
    };
    const wanted = isFinalista
      ? editForm.companions.map((c) => ({
          ...c,
          nombre: c.nombre.trim(),
          apellidos: c.apellidos.trim(),
          email: c.email.trim(),
        }))
      : [];
    const original = editing.companions;
    const removedIds = original.filter((o) => !wanted.some((w) => w.id === o.id)).map((o) => o.id);
    const changed = wanted.filter((w) => {
      const o = original.find((x) => x.id === w.id);
      return o && (o.nombre !== w.nombre || o.apellidos !== w.apellidos || o.email !== w.email);
    });
    const added = wanted.filter((w) => w.id === null).map((w) => ({ ...w, id: newId() }));

    setSaving(true);
    try {
      const table = (name: string) => supabase.schema('premios').from(name);

      const { error: updateError } = await table('gala_registrations').update(patch).eq('id', editing.id);
      if (updateError) throw updateError;

      if (removedIds.length > 0) {
        const { error: delError } = await table('gala_companions').delete().in('id', removedIds);
        if (delError) throw delError;
      }
      for (const c of changed) {
        const { error: compError } = await table('gala_companions')
          .update({ nombre: c.nombre, apellidos: c.apellidos, email: c.email })
          .eq('id', c.id);
        if (compError) throw compError;
      }
      if (added.length > 0) {
        const { error: insError } = await table('gala_companions').insert(
          added.map((c) => ({
            id: c.id,
            registration_id: editing.id,
            nombre: c.nombre,
            apellidos: c.apellidos,
            email: c.email,
          }))
        );
        if (insError) throw insError;
      }

      const companions: Companion[] = wanted.map((w) => {
        const id = w.id ?? added.find((a) => a.key === w.key)!.id;
        const prev = original.find((o) => o.id === id);
        return {
          id,
          acreditado: prev ? prev.acreditado : false,
          nombre: w.nombre,
          apellidos: w.apellidos,
          email: w.email,
        };
      });

      setRegistrations((rs) => rs.map((r) => (r.id === editing.id ? { ...r, ...patch, companions } : r)));
      notify('Cambios guardados');
      closeEdit();
    } catch (err) {
      console.error('Error al editar inscripción de gala:', err);
      setError('No se han podido guardar los cambios. Se han recargado los datos; revisa e inténtalo de nuevo.');
      closeEdit();
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const deleteRegistration = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      const { error: delError } = await supabase
        .schema('premios')
        .from('gala_registrations')
        .delete()
        .eq('id', deleting.id);
      if (delError) throw delError;

      setRegistrations((rs) => rs.filter((r) => r.id !== deleting.id));
      notify(`Inscripción de ${deleting.nombre} ${deleting.apellidos} eliminada`);
      setDeleting(null);
    } catch (err) {
      console.error('Error al eliminar inscripción de gala:', err);
      setError('No se ha podido eliminar la inscripción. Inténtalo de nuevo.');
      setDeleting(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/inscripcion-gala`);
      setLinkCopied(true);
      notify('Enlace de inscripción copiado');
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      notify('No se ha podido copiar el enlace');
    }
  };

  const onTabKeyDown = (e: React.KeyboardEvent) => {
    const order: Tab[] = ['listado', 'acreditar'];
    const idx = order.indexOf(tab);
    let next: Tab | null = null;
    if (e.key === 'ArrowRight') next = order[(idx + 1) % order.length];
    if (e.key === 'ArrowLeft') next = order[(idx - 1 + order.length) % order.length];
    if (e.key === 'Home') next = order[0];
    if (e.key === 'End') next = order[order.length - 1];
    if (next) {
      e.preventDefault();
      setTab(next);
      tabRefs.current[next]?.focus();
    }
  };

  const stats = useMemo(() => {
    const companions = registrations.reduce((n, r) => n + r.companions.length, 0);
    const total = registrations.length + companions;
    const done = registrations.reduce((n, r) => n + partyDone(r), 0);
    const count = (t: Tipo) => registrations.filter((r) => r.tipo === t).length;
    return {
      companions,
      total,
      done,
      pending: total - done,
      pct: total === 0 ? 0 : Math.round((done / total) * 100),
      byTipo: {
        finalista: count('finalista'),
        invitado_novagob: count('invitado_novagob'),
        invitado_madrid_talento: count('invitado_madrid_talento'),
        invitado_patrocinador: count('invitado_patrocinador'),
      } as Record<Tipo, number>,
      partiesPending: registrations.filter((r) => partyDone(r) < partySize(r)).length,
      partiesWithDone: registrations.filter((r) => partyDone(r) > 0).length,
    };
  }, [registrations]);

  const listadoRows = useMemo(
    () =>
      registrations.filter((r) => (tipoFilter === 'todos' || r.tipo === tipoFilter) && matches(r, search)),
    [registrations, tipoFilter, search]
  );

  const acreditarRows = useMemo(
    () =>
      registrations
        .filter((r) => {
          if (estado === 'pendientes' && partyDone(r) === partySize(r)) return false;
          if (estado === 'acreditados' && partyDone(r) === 0) return false;
          return matches(r, acreditarSearch);
        })
        .sort(
          (a, b) =>
            a.apellidos.localeCompare(b.apellidos, 'es') || a.nombre.localeCompare(b.nombre, 'es')
        ),
    [registrations, estado, acreditarSearch]
  );

  if (!isNovagobTeam) {
    return (
      <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
        <div className="card">
          <p>No tienes permisos para acceder a esta página.</p>
        </div>
      </div>
    );
  }

  const isFinalistaEdit = editForm?.tipo === 'finalista';

  const searchBox = (
    value: string,
    onChange: (v: string) => void,
    label: string,
    placeholder: string
  ) => (
    <div className="mg-search">
      <Search size={20} aria-hidden="true" />
      <label className="mg-sr-only" htmlFor={`mg-search-${label}`}>
        {label}
      </label>
      <input
        id={`mg-search-${label}`}
        type="search"
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button
          type="button"
          className="mg-search-clear"
          onClick={() => onChange('')}
          aria-label="Borrar búsqueda"
        >
          <X size={18} aria-hidden="true" />
        </button>
      )}
    </div>
  );

  const statusChip = (done: boolean) => (
    <span className="mg-status" data-done={done}>
      {done ? <CircleCheck size={16} aria-hidden="true" /> : <Clock size={16} aria-hidden="true" />}
      {done ? 'Acreditado' : 'Pendiente'}
    </span>
  );

  const partyChip = (r: Registration) => {
    const done = partyDone(r);
    const size = partySize(r);
    if (done === 0) return statusChip(false);
    if (done === size) return statusChip(true);
    return (
      <span className="mg-status" data-done="partial">
        <Clock size={16} aria-hidden="true" />
        {done} de {size} acreditados
      </span>
    );
  };

  const companionsLine = (r: Registration) =>
    r.companions.length > 0 && (
      <span className="mg-party">
        <Users size={16} aria-hidden="true" />
        <span>
          <span className="mg-sr-only">Acompañantes: </span>
          {r.companions.map((c) => `${c.nombre} ${c.apellidos}`).join(', ')}
        </span>
      </span>
    );

  return (
    <div className="container dashboard mg">
      <header className="mg-head">
        <div>
          <h1 className="mg-title">Gestión de Gala</h1>
          <p className="mg-subtitle">Gestión inscripciones gala premios</p>
        </div>
        <button type="button" className="mg-btn mg-btn-ghost" onClick={copyLink}>
          {linkCopied ? <Check size={18} aria-hidden="true" /> : <Link2 size={18} aria-hidden="true" />}
          {linkCopied ? 'Enlace copiado' : 'Copiar enlace de inscripción'}
        </button>
      </header>

      <div className="mg-tabs" role="tablist" aria-label="Secciones de la gestión de gala" onKeyDown={onTabKeyDown}>
        <button
          ref={(el) => {
            tabRefs.current.listado = el;
          }}
          type="button"
          role="tab"
          id="mg-tab-listado"
          aria-selected={tab === 'listado'}
          aria-controls="mg-panel-listado"
          tabIndex={tab === 'listado' ? 0 : -1}
          className="mg-tab"
          onClick={() => setTab('listado')}
        >
          Inscripciones
          <span className="mg-tab-count">{registrations.length}</span>
        </button>
        <button
          ref={(el) => {
            tabRefs.current.acreditar = el;
          }}
          type="button"
          role="tab"
          id="mg-tab-acreditar"
          aria-selected={tab === 'acreditar'}
          aria-controls="mg-panel-acreditar"
          tabIndex={tab === 'acreditar' ? 0 : -1}
          className="mg-tab"
          onClick={() => setTab('acreditar')}
        >
          Acreditación
          <span className="mg-tab-count">
            {stats.done}/{stats.total}
          </span>
        </button>
      </div>

      {error && (
        <div className="mg-alert" role="alert">
          <span>{error}</span>
          <button type="button" className="mg-btn mg-btn-ghost mg-btn-sm" onClick={loadData}>
            Reintentar
          </button>
        </div>
      )}

      {loading ? (
        <div aria-busy="true" aria-label="Cargando inscripciones" className="mg-list">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="mg-skeleton" />
          ))}
        </div>
      ) : (
        <>
          {/* ================= INSCRIPCIONES ================= */}
          <section
            role="tabpanel"
            id="mg-panel-listado"
            aria-labelledby="mg-tab-listado"
            hidden={tab !== 'listado'}
          >
            <div className="mg-summary">
              <div className="mg-summary-main">
                <span className="mg-figure">{stats.total}</span>
                <span className="mg-figure-label">personas confirmadas en total</span>
              </div>
              <dl className="mg-breakdown">
                <div>
                  <dt>Inscripciones</dt>
                  <dd>{registrations.length}</dd>
                </div>
                <div>
                  <dt>Acompañantes</dt>
                  <dd>{stats.companions}</dd>
                </div>
                {(Object.keys(TIPO_LABELS) as Tipo[]).map((t) => (
                  <div key={t}>
                    <dt>{TIPO_SHORT[t]}</dt>
                    <dd>{stats.byTipo[t]}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mg-tools">
              {searchBox(search, setSearch, 'inscripciones', 'Buscar por nombre, email, institución o acompañante')}
              <div className="mg-chips" role="group" aria-label="Filtrar por tipo de inscripción">
                <button
                  type="button"
                  className="mg-chip"
                  aria-pressed={tipoFilter === 'todos'}
                  onClick={() => setTipoFilter('todos')}
                >
                  Todos <span className="mg-chip-n">{registrations.length}</span>
                </button>
                {(Object.keys(TIPO_LABELS) as Tipo[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="mg-chip"
                    aria-pressed={tipoFilter === t}
                    onClick={() => setTipoFilter(t)}
                  >
                    {TIPO_SHORT[t]} <span className="mg-chip-n">{stats.byTipo[t]}</span>
                  </button>
                ))}
              </div>
            </div>

            {registrations.length === 0 ? (
              <div className="mg-empty">
                <h3>Todavía no hay inscripciones</h3>
                <p>Cuando alguien se inscriba con el enlace de la gala aparecerá aquí al instante.</p>
              </div>
            ) : listadoRows.length === 0 ? (
              <div className="mg-empty">
                <h3>Sin resultados</h3>
                <p>Ninguna inscripción coincide con la búsqueda o el filtro actual.</p>
                <button
                  type="button"
                  className="mg-btn mg-btn-ghost mg-btn-sm"
                  style={{ marginTop: 16 }}
                  onClick={() => {
                    setSearch('');
                    setTipoFilter('todos');
                  }}
                >
                  Quitar filtros
                </button>
              </div>
            ) : (
              <>
                <p className="mg-count" aria-live="polite">
                  Mostrando {listadoRows.length} de {registrations.length} inscripciones
                </p>
                <ul className="mg-list">
                  {listadoRows.map((r) => (
                    <li key={r.id} className="mg-row" data-done={r.acreditado}>
                      <div className="mg-person">
                        <span className="mg-avatar" aria-hidden="true">
                          {initials(r)}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div className="mg-name">
                            {r.nombre} {r.apellidos}
                          </div>
                          <div className="mg-sub">{r.email}</div>
                          {r.telefono && <div className="mg-sub">Tel. {r.telefono}</div>}
                        </div>
                      </div>

                      <div className="mg-meta">
                        <span className="mg-tipo" data-tipo={r.tipo}>
                          {tipoLabel(r)}
                        </span>
                        {companionsLine(r)}
                      </div>

                      <div className="mg-actions">
                        {partyChip(r)}
                        <button
                          type="button"
                          className="mg-icon-btn"
                          onClick={() => openEdit(r)}
                          aria-label={`Editar inscripción de ${r.nombre} ${r.apellidos}`}
                          title="Editar"
                        >
                          <Pencil size={18} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="mg-icon-btn mg-icon-btn-danger"
                          onClick={() => setDeleting(r)}
                          aria-label={`Eliminar inscripción de ${r.nombre} ${r.apellidos}`}
                          title="Eliminar"
                        >
                          <Trash2 size={18} aria-hidden="true" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          {/* ================= ACREDITACIÓN ================= */}
          <section
            role="tabpanel"
            id="mg-panel-acreditar"
            aria-labelledby="mg-tab-acreditar"
            hidden={tab !== 'acreditar'}
          >
            <div className="mg-progress-card">
              <div className="mg-progress-top">
                <div className="mg-progress-big">
                  {stats.done} <small>de {stats.total} personas acreditadas</small>
                </div>
                <div className="mg-progress-meta">
                  Faltan <strong>{stats.pending}</strong> por llegar · {stats.pct}%
                </div>
              </div>
              <div
                className="mg-bar"
                role="progressbar"
                aria-label="Personas acreditadas"
                aria-valuemin={0}
                aria-valuemax={stats.total}
                aria-valuenow={stats.done}
              >
                <span style={{ transform: `scaleX(${stats.pct / 100})` }} />
              </div>
            </div>

            <div className="mg-tools">
              {searchBox(acreditarSearch, setAcreditarSearch, 'acreditacion', 'Busca a quien llega: nombre, apellidos o email')}
              <div className="mg-chips" role="group" aria-label="Filtrar por estado de acreditación">
                {(
                  [
                    ['pendientes', 'Pendientes', stats.partiesPending],
                    ['acreditados', 'Acreditados', stats.partiesWithDone],
                    ['todos', 'Todos', registrations.length],
                  ] as [Estado, string, number][]
                ).map(([value, label, n]) => (
                  <button
                    key={value}
                    type="button"
                    className="mg-chip"
                    aria-pressed={estado === value}
                    onClick={() => setEstado(value)}
                  >
                    {label} <span className="mg-chip-n">{n}</span>
                  </button>
                ))}
              </div>
            </div>

            {acreditarRows.length === 0 ? (
              <div className="mg-empty">
                <h3>
                  {registrations.length === 0
                    ? 'Todavía no hay inscripciones'
                    : acreditarSearch
                    ? 'Nadie coincide con la búsqueda'
                    : estado === 'pendientes'
                    ? 'No queda nadie por acreditar'
                    : 'Aún no hay nadie acreditado'}
                </h3>
                <p>
                  {acreditarSearch
                    ? 'Prueba con solo el apellido o revisa el filtro de estado.'
                    : 'Cambia el filtro de estado para ver el resto de inscripciones.'}
                </p>
              </div>
            ) : (
              <>
                <p className="mg-count" aria-live="polite">
                  {plural(acreditarRows.length, 'inscripción', 'inscripciones')}
                </p>
                <ul className="mg-list">
                  {acreditarRows.map((r) => {
                    const members: { key: string; comp: Companion | null; name: string; sub: string; done: boolean; avatar: string }[] = [
                      {
                        key: r.id,
                        comp: null,
                        name: `${r.nombre} ${r.apellidos}`,
                        sub: `${tipoLabel(r)}${r.institucion ? ` · ${r.institucion}` : ''}`,
                        done: r.acreditado,
                        avatar: initials(r),
                      },
                      ...r.companions.map((c) => ({
                        key: c.id,
                        comp: c,
                        name: `${c.nombre} ${c.apellidos}`,
                        sub: `Acompañante de ${r.nombre} ${r.apellidos}`,
                        done: c.acreditado,
                        avatar: `${c.nombre.charAt(0)}${c.apellidos.charAt(0)}`.toUpperCase(),
                      })),
                    ];
                    return (
                      <li key={r.id} className="mg-card" data-done={partyDone(r) === partySize(r)}>
                        {members.map((m) => (
                          <div key={m.key} className="mg-member" data-companion={m.comp !== null} data-done={m.done}>
                            <div className="mg-person">
                              <span className="mg-avatar" aria-hidden="true">
                                {m.avatar}
                              </span>
                              <div style={{ minWidth: 0 }}>
                                <div className="mg-name">{m.name}</div>
                                <div className="mg-sub">{m.sub}</div>
                              </div>
                            </div>
                            <div className="mg-actions">
                              {statusChip(m.done)}
                              <button
                                type="button"
                                className={`mg-btn mg-btn-sm ${m.done ? 'mg-btn-ghost' : 'mg-btn-primary'}`}
                                onClick={() => setAcreditado(r, m.comp, !m.done)}
                                disabled={updatingId !== null}
                                aria-label={`${m.done ? 'Quitar acreditación a' : 'Acreditar a'} ${m.name}`}
                              >
                                {m.done ? 'Quitar' : 'Acreditar'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </section>
        </>
      )}

      {/* ================= EDICIÓN ================= */}
      <dialog
        ref={dialogRef}
        className="mg-dialog"
        aria-labelledby="mg-edit-title"
        onClose={() => {
          if (editing) closeEdit();
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !saving) closeEdit();
        }}
      >
        {editing && editForm && (
          <form onSubmit={saveEdit}>
            <div className="mg-dialog-head">
              <div>
                <h2 id="mg-edit-title">Editar inscripción</h2>
                <p>
                  {editing.nombre} {editing.apellidos} · {plural(partySize(editing), 'persona', 'personas')}
                </p>
              </div>
              <button
                type="button"
                className="mg-search-clear"
                style={{ position: 'static', transform: 'none' }}
                onClick={closeEdit}
                aria-label="Cerrar sin guardar"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            <div className="mg-dialog-body">
              <div className="mg-field mg-field-full">
                <label htmlFor="mg-f-tipo">Tipo de inscripción</label>
                <select
                  id="mg-f-tipo"
                  value={editForm.tipo}
                  onChange={(e) => setEditForm({ ...editForm, tipo: e.target.value as Tipo })}
                >
                  {(Object.keys(TIPO_LABELS) as Tipo[]).map((t) => (
                    <option key={t} value={t}>
                      {TIPO_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mg-field">
                <label htmlFor="mg-f-nombre">Nombre</label>
                <input
                  id="mg-f-nombre"
                  required
                  autoComplete="off"
                  value={editForm.nombre}
                  onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })}
                />
              </div>
              <div className="mg-field">
                <label htmlFor="mg-f-apellidos">Apellidos</label>
                <input
                  id="mg-f-apellidos"
                  required
                  autoComplete="off"
                  value={editForm.apellidos}
                  onChange={(e) => setEditForm({ ...editForm, apellidos: e.target.value })}
                />
              </div>

              <div className="mg-field">
                <label htmlFor="mg-f-email">Email</label>
                <input
                  id="mg-f-email"
                  type="email"
                  required
                  autoComplete="off"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
              {isFinalistaEdit && (
                <div className="mg-field">
                  <label htmlFor="mg-f-tel">Teléfono de contacto</label>
                  <input
                    id="mg-f-tel"
                    type="tel"
                    required
                    autoComplete="off"
                    value={editForm.telefono}
                    onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })}
                  />
                </div>
              )}

              <div className="mg-field">
                <label htmlFor="mg-f-cargo">Cargo / Puesto</label>
                <input
                  id="mg-f-cargo"
                  autoComplete="off"
                  value={editForm.cargo}
                  onChange={(e) => setEditForm({ ...editForm, cargo: e.target.value })}
                />
              </div>
              <div className="mg-field">
                <label htmlFor="mg-f-inst">Institución / Organización</label>
                <input
                  id="mg-f-inst"
                  autoComplete="off"
                  value={editForm.institucion}
                  onChange={(e) => setEditForm({ ...editForm, institucion: e.target.value })}
                />
              </div>

              {isFinalistaEdit && (
                <div className="mg-field mg-field-full">
                  <label htmlFor="mg-f-premio">Categoría del premio</label>
                  <select
                    id="mg-f-premio"
                    value={editForm.categoriaPremio}
                    onChange={(e) => setEditForm({ ...editForm, categoriaPremio: e.target.value })}
                  >
                    <option value="">Sin indicar</option>
                    {(() => {
                      const all = [...premioCategories, SPECIAL_AWARD_CATEGORY];
                      return editForm.categoriaPremio && !all.includes(editForm.categoriaPremio)
                        ? [editForm.categoriaPremio, ...all]
                        : all;
                    })().map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {isFinalistaEdit && editForm.categoriaPremio === SPECIAL_AWARD_CATEGORY && (
                <div className="mg-field mg-field-full">
                  <label htmlFor="mg-f-especial">¿Qué premio especial?</label>
                  <select
                    id="mg-f-especial"
                    value={editForm.premioEspecial}
                    onChange={(e) => setEditForm({ ...editForm, premioEspecial: e.target.value })}
                  >
                    <option value="">Sin indicar</option>
                    {SPECIAL_AWARDS.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {editForm.tipo === 'invitado_patrocinador' && (
                <div className="mg-field mg-field-full">
                  <label htmlFor="mg-f-patro">Patrocinador</label>
                  <select
                    id="mg-f-patro"
                    value={editForm.patrocinador}
                    onChange={(e) => setEditForm({ ...editForm, patrocinador: e.target.value })}
                  >
                    <option value="">Sin indicar</option>
                    {GALA_SPONSORS.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {!isFinalistaEdit && (
                <>
                  <div className="mg-field">
                    <label htmlFor="mg-f-cat">Categoría de puesto</label>
                    <select
                      id="mg-f-cat"
                      value={editForm.categoriaPuesto}
                      onChange={(e) => setEditForm({ ...editForm, categoriaPuesto: e.target.value })}
                    >
                      <option value="">Sin indicar</option>
                      {JOB_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mg-field">
                    <label htmlFor="mg-f-adm">Tipo de administración</label>
                    <select
                      id="mg-f-adm"
                      value={editForm.tipoAdministracion}
                      onChange={(e) => setEditForm({ ...editForm, tipoAdministracion: e.target.value })}
                    >
                      <option value="">Sin indicar</option>
                      {ADMINISTRATION_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {isFinalistaEdit && (
                <fieldset className="mg-field-full mg-comps">
                  <legend>Acompañantes ({editForm.companions.length} de {MAX_COMPANIONS})</legend>
                  {editForm.companions.length === 0 && (
                    <p className="mg-comps-empty">Este finalista no tiene acompañantes.</p>
                  )}
                  {editForm.companions.map((c, i) => {
                    const upd = (patchC: Partial<EditCompanion>) =>
                      setEditForm({
                        ...editForm,
                        companions: editForm.companions.map((x) => (x.key === c.key ? { ...x, ...patchC } : x)),
                      });
                    return (
                      <div key={c.key} className="mg-comp">
                        <div className="mg-comp-head">
                          <strong>Acompañante {i + 1}</strong>
                          <button
                            type="button"
                            className="mg-icon-btn mg-icon-btn-danger"
                            title="Quitar acompañante"
                            onClick={() =>
                              setEditForm({
                                ...editForm,
                                companions: editForm.companions.filter((x) => x.key !== c.key),
                              })
                            }
                            aria-label={`Quitar acompañante ${i + 1}`}
                          >
                            <Trash2 size={18} aria-hidden="true" />
                          </button>
                        </div>
                        <div className="mg-comp-grid">
                          <div className="mg-field">
                            <label htmlFor={`mg-c-n-${c.key}`}>Nombre</label>
                            <input
                              id={`mg-c-n-${c.key}`}
                              required
                              autoComplete="off"
                              value={c.nombre}
                              onChange={(e) => upd({ nombre: e.target.value })}
                            />
                          </div>
                          <div className="mg-field">
                            <label htmlFor={`mg-c-a-${c.key}`}>Apellidos</label>
                            <input
                              id={`mg-c-a-${c.key}`}
                              required
                              autoComplete="off"
                              value={c.apellidos}
                              onChange={(e) => upd({ apellidos: e.target.value })}
                            />
                          </div>
                          <div className="mg-field mg-field-full">
                            <label htmlFor={`mg-c-e-${c.key}`}>Email</label>
                            <input
                              id={`mg-c-e-${c.key}`}
                              type="email"
                              required
                              autoComplete="off"
                              value={c.email}
                              onChange={(e) => upd({ email: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {editForm.companions.length < MAX_COMPANIONS && (
                    <button
                      type="button"
                      className="mg-btn mg-btn-ghost mg-btn-sm"
                      onClick={() =>
                        setEditForm({
                          ...editForm,
                          companions: [
                            ...editForm.companions,
                            { key: newId(), id: null, nombre: '', apellidos: '', email: '' },
                          ],
                        })
                      }
                    >
                      <Plus size={16} aria-hidden="true" />
                      Añadir acompañante
                    </button>
                  )}
                </fieldset>
              )}

              {!isFinalistaEdit && editing.companions.length > 0 && (
                <p className="mg-field-full mg-note" role="note">
                  Solo los finalistas llevan acompañantes: al guardar se eliminarán los{' '}
                  {editing.companions.length} acompañante{editing.companions.length === 1 ? '' : 's'} de esta
                  inscripción.
                </p>
              )}
            </div>

            <div className="mg-dialog-foot">
              <button type="button" className="mg-btn mg-btn-ghost" onClick={closeEdit} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="mg-btn mg-btn-primary" disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        )}
      </dialog>

      <dialog
        ref={deleteDialogRef}
        className="mg-dialog mg-dialog-sm"
        aria-labelledby="mg-del-title"
        onClose={() => setDeleting(null)}
        onClick={(e) => {
          if (e.target === e.currentTarget && !deleteBusy) setDeleting(null);
        }}
      >
        {deleting && (
          <div>
            <div className="mg-dialog-head">
              <div>
                <h2 id="mg-del-title">Eliminar inscripción</h2>
                <p>Esta acción no se puede deshacer.</p>
              </div>
            </div>
            <div className="mg-dialog-text">
              ¿Eliminar la inscripción de <strong>{deleting.nombre} {deleting.apellidos}</strong>
              {deleting.companions.length > 0
                ? ` y ${plural(deleting.companions.length, 'acompañante', 'acompañantes')}`
                : ''}
              ?
            </div>
            <div className="mg-dialog-foot">
              <button
                type="button"
                className="mg-btn mg-btn-ghost"
                onClick={() => setDeleting(null)}
                disabled={deleteBusy}
                autoFocus
              >
                Cancelar
              </button>
              <button type="button" className="mg-btn mg-btn-danger-solid" onClick={deleteRegistration} disabled={deleteBusy}>
                {deleteBusy ? 'Eliminando…' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        )}
      </dialog>

      <div aria-live="polite" role="status">
        {toast && (
          <div className="mg-toast">
            <CircleCheck size={18} color="#4ade80" aria-hidden="true" />
            {toast.msg}
            {toast.undo && (
              <button
                type="button"
                className="mg-toast-undo"
                onClick={() => {
                  const undo = toast.undo;
                  setToast(null);
                  undo?.();
                }}
              >
                Deshacer
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageGalaRegistrations;
